import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const CategoryModal = ({ category, onClose, highlightProductId }) => {
  const closeButtonRef = useRef(null);
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(highlightProductId ?? null);
  const activeProduct = category.products.find((product) => product.id === activeId);

  const onCloseRef = useRef(onClose);
  const scrollYRef = useRef(null);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (scrollYRef.current == null) {
      scrollYRef.current = window.scrollY;
    }
    const scrollY = scrollYRef.current;
    const { documentElement: html, body } = document;

    html.classList.add("modal-open");
    body.classList.add("modal-open");
    body.style.top = `-${scrollY}px`;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };

    document.addEventListener("keydown", handleKeyDown);

    if (highlightProductId) {
      const item = document.getElementById(`category-product-${highlightProductId}`);
      const list = item?.closest(".category-modal-list-scroll");
      if (item && list) {
        const top =
          item.getBoundingClientRect().top -
          list.getBoundingClientRect().top +
          list.scrollTop;
        list.scrollTop = Math.max(0, top - 12);
      }
    }

    return () => {
      html.classList.remove("modal-open");
      body.classList.remove("modal-open");
      body.style.top = "";
      const previousScrollBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = previousScrollBehavior;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div
      className="category-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="category-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-modal-title"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="category-modal-close"
          onClick={onClose}
          aria-label={t("modal.close")}
        >
          ×
        </button>
        <h2 id="category-modal-title">{category.name}</h2>
        <div className="category-modal-body">
          <div className="category-modal-list-scroll">
            <ul className="category-product-list">
              {category.products.map((product) => (
                <li
                  key={product.id}
                  id={`category-product-${product.id}`}
                  className={`category-product-item${
                    product.id === highlightProductId ? " is-highlighted" : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`category-product-button${
                      product.id === activeId ? " is-active" : ""
                    }`}
                    aria-pressed={product.id === activeId}
                    onClick={() => setActiveId(product.id)}
                  >
                    <span className="category-product-name">{product.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <aside className="category-product-detail" aria-live="polite">
            {activeProduct ? (
              <div key={activeProduct.id} className="category-product-detail-card">
                <img src={activeProduct.image} alt={activeProduct.name} />
                <p>{activeProduct.description}</p>
              </div>
            ) : (
              <p className="category-product-detail-empty">{t("products.detailPrompt")}</p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CategoryModal;
